import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import "../css/OwnershipChangeDialogs.css";

// Dialogs for ownership changes (client type change, Reset, Edit diagram removals), in the same
// style as the app's "Change client type" modal: title + hint, light content, footer actions.

const KIND_LABEL = { individual: "Individual", company: "Company", trust: "Trust", foundation: "Foundation" };

function DialogShell({ open, title, hint, onClose, children, footer }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return createPortal(
    <div className="ownDlgBackdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="ownDlg" role="dialog" aria-modal="true" aria-label={title}>
        <div className="ownDlgHead">
          <div>
            <div className="ownDlgTitle">{title}</div>
            {hint ? <div className="ownDlgHint">{hint}</div> : null}
          </div>
          {onClose ? (
            <button type="button" className="ownDlgClose" onClick={onClose} aria-label="Close">
              <i className="bi bi-x-lg" aria-hidden="true" />
            </button>
          ) : null}
        </div>
        <div className="ownDlgBody">{children}</div>
        <div className="ownDlgFooter">{footer}</div>
      </div>
    </div>,
    document.body
  );
}

/**
 * A choice between options (selectable cards) confirmed with one button, or — without options —
 * a warning with a confirm button. onConfirm(value) receives the chosen option's value.
 */
export function OwnershipChoiceDialog({ open, title, hint, message, options = [], confirmLabel = "Continue", danger, onConfirm, onCancel }) {
  const [value, setValue] = useState(options[0]?.value || "");
  useEffect(() => { if (open) setValue(options[0]?.value || ""); }, [open]); // eslint-disable-line
  const chosen = options.find((o) => o.value === value);
  const isDanger = chosen ? !!chosen.danger : !!danger;
  return (
    <DialogShell
      open={open}
      title={title}
      hint={hint}
      onClose={onCancel}
      footer={(
        <>
          <button type="button" className="ownDlgBtn" onClick={onCancel}>Cancel</button>
          <button type="button" className={`ownDlgBtn ownDlgBtnPrimary${isDanger ? " is-danger" : ""}`} onClick={() => onConfirm?.(value)}>
            {chosen?.confirmLabel || confirmLabel}
          </button>
        </>
      )}
    >
      {message ? <p className="ownDlgMessage">{message}</p> : null}
      {options.length ? (
        <div className="ownDlgOptions" role="radiogroup">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={value === option.value}
              className={`ownDlgOption${value === option.value ? " is-selected" : ""}`}
              onClick={() => setValue(option.value)}
            >
              <span className="ownDlgRadio" aria-hidden="true" />
              <span className="ownDlgOptionCopy">
                <span className="ownDlgOptionTitle">{option.label}</span>
                {option.hint ? <span className="ownDlgOptionHint">{option.hint}</span> : null}
                {option.note ? <span className="ownDlgOptionNote">{option.note}</span> : null}
              </span>
            </button>
          ))}
        </div>
      ) : null}
    </DialogShell>
  );
}

/**
 * Warning before removing ownership parties that have approved documents or linked signatories.
 * onApply({ keepDocuments, signatoryChoices }) — signatoryChoices: { [signatoryRowId]: "keep" | "remove" }.
 */
export function OwnershipRemovalDialog({ open, title = "Apply changes?", intro, parties = [], signatories = [], showDocumentChoice, applyLabel = "Apply", backLabel = "Back to the diagram", signatoriesTitle = "Authorised signatories linked to these parties", kindLabels = KIND_LABEL, onApply, onBack, busy }) {
  const [keepDocuments, setKeepDocuments] = useState(true);
  const [choices, setChoices] = useState({});
  useEffect(() => {
    if (!open) return;
    setKeepDocuments(true);
    setChoices(Object.fromEntries(signatories.map((s) => [String(s._rowId || ""), "keep"])));
  }, [open, signatories]);

  const listed = parties.filter((p) => p.approvedCount || p.signatory);
  return (
    <DialogShell
      open={open}
      title={title}
      hint={intro || "The following will be removed from the ownership structure."}
      onClose={busy ? undefined : onBack}
      footer={(
        <>
          <button type="button" className="ownDlgBtn" disabled={busy} onClick={onBack}>{backLabel}</button>
          <button type="button" className="ownDlgBtn ownDlgBtnPrimary" disabled={busy} onClick={() => onApply?.({ keepDocuments, signatoryChoices: choices })}>
            {busy ? "Applying…" : applyLabel}
          </button>
        </>
      )}
    >
      <div className="ownDlgList">
        {listed.map((party) => (
          <div key={party.key} className="ownDlgListRow">
            <span className="ownDlgName">{party.name}</span>
            <span className="ownDlgChip">{kindLabels[party.kind] || party.kind}</span>
            {party.parentName ? <span className="ownDlgMeta">{party.parentName}</span> : null}
            {party.approvedCount ? (
              <span className="ownDlgMeta">{party.approvedCount} approved document{party.approvedCount > 1 ? "s" : ""}</span>
            ) : null}
          </div>
        ))}
      </div>

      {showDocumentChoice ? (
        <div className="ownDlgGroup">
          <div className="ownDlgGroupTitle">Documents</div>
          <label className="ownDlgCheck">
            <input type="checkbox" checked={keepDocuments} onChange={() => setKeepDocuments(true)} />
            <span>Documents of these parties will be kept (as former owners)</span>
          </label>
          <label className="ownDlgCheck">
            <input type="checkbox" checked={!keepDocuments} onChange={() => setKeepDocuments(false)} />
            <span>Delete documents of these parties</span>
          </label>
        </div>
      ) : null}

      {signatories.length ? (
        <div className="ownDlgGroup">
          <div className="ownDlgGroupTitle">{signatoriesTitle}</div>
          {signatories.map((s) => {
            const id = String(s._rowId || "");
            const remove = choices[id] === "remove";
            return (
              <div key={id} className="ownDlgSignatory">
                <span className="ownDlgName">{s.name || "Unnamed signatory"}</span>
                <span className="ownDlgSegment" role="radiogroup" aria-label={`${s.name || "Signatory"}`}>
                  <button type="button" role="radio" aria-checked={!remove} className={!remove ? "is-selected" : ""}
                    onClick={() => setChoices((c) => ({ ...c, [id]: "keep" }))}>Keep as signatory</button>
                  <button type="button" role="radio" aria-checked={remove} className={remove ? "is-selected" : ""}
                    onClick={() => setChoices((c) => ({ ...c, [id]: "remove" }))}>Remove as signatory</button>
                </span>
              </div>
            );
          })}
        </div>
      ) : null}
    </DialogShell>
  );
}
